/**
 * The status vocabulary.
 *
 * DESIGN_LANGUAGE.md §1 ("Consistent"): *"A 'check status' indicator in a PR, in
 * a commit list and in a run list is one component with one vocabulary."* This
 * is that component, and the vocabulary is built from the enums the API
 * actually sends rather than from a set of adjectives chosen here — `CheckState`
 * and `RunState` from `docs/api/openapi/v1.yaml`, plus the severity scale from
 * `SecuritySeverity`.
 *
 * The §8 rule is enforced structurally rather than remembered: every tone comes
 * with a glyph, the glyph is not optional, and the accessible text is built from
 * the state's name. There is no way to render one of these as a bare coloured
 * dot, which is the failure mode the rule exists to prevent.
 */

import type { ReactNode } from 'react';

import { cx } from '~/lib/cx';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle,
  Clock,
  Dash,
  Dot,
  Info,
  Slash,
  type IconProps,
} from './icons';

import styles from './Status.module.css';

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'done';

/* ---------------------------------------------------------------------- *
 * The state vocabulary
 * ---------------------------------------------------------------------- */

/**
 * Every state this interface can show, mapped once to a tone, a glyph and the
 * words a screen reader hears.
 *
 * `absent` is in here and is not in `CheckState`, deliberately. The contract is
 * emphatic about it: *"A check nobody has reported is not pending — it is
 * absent, and absence never satisfies a requirement."* Rendering an unreported
 * check as pending would tell the user somebody is working on it. Nobody is.
 */
const STATES = {
  // CheckState
  pending: { tone: 'warning', icon: Clock, label: 'Pending' },
  success: { tone: 'success', icon: CheckCircle, label: 'Passed' },
  failure: { tone: 'danger', icon: AlertOctagon, label: 'Failed' },
  error: { tone: 'danger', icon: AlertTriangle, label: 'Errored' },
  cancelled: { tone: 'done', icon: Slash, label: 'Cancelled' },
  absent: { tone: 'neutral', icon: Dash, label: 'Not reported' },

  // RunState — `queued` and `running` are distinct facts and the contract
  // keeps them apart, so this does too.
  queued: { tone: 'neutral', icon: Clock, label: 'Queued' },
  running: { tone: 'info', icon: Dot, label: 'Running' },
  succeeded: { tone: 'success', icon: CheckCircle, label: 'Succeeded' },
  failed: { tone: 'danger', icon: AlertOctagon, label: 'Failed' },

  // SecuritySeverity
  critical: { tone: 'danger', icon: AlertOctagon, label: 'Critical' },
  high: { tone: 'danger', icon: AlertTriangle, label: 'High' },
  medium: { tone: 'warning', icon: AlertTriangle, label: 'Medium' },
  low: { tone: 'info', icon: Info, label: 'Low' },
  info: { tone: 'neutral', icon: Info, label: 'Informational' },

  // RepositoryState / StorageState, where they are worth showing
  active: { tone: 'success', icon: CheckCircle, label: 'Active' },
  archived: { tone: 'done', icon: Slash, label: 'Archived' },
  initializing: { tone: 'info', icon: Clock, label: 'Initializing' },
  ready: { tone: 'success', icon: CheckCircle, label: 'Ready' },
} as const satisfies Record<
  string,
  { tone: Tone; icon: (p: IconProps) => ReactNode; label: string }
>;

export type StatusName = keyof typeof STATES;

export function isKnownStatus(value: string): value is StatusName {
  return Object.hasOwn(STATES, value);
}

export interface StatusIndicatorProps {
  status: StatusName;
  /**
   * Show the state's name beside the glyph.
   *
   * When false the name is still announced — it moves into a visually hidden
   * span rather than disappearing. §8 forbids colour as the only signal and a
   * glyph on its own is still a signal a screen reader cannot read.
   */
  showLabel?: boolean;
  /** Replace the state's own name, for a count or a qualifier: "3 critical". */
  children?: ReactNode;
  className?: string;
}

export function StatusIndicator({
  status,
  showLabel = true,
  children,
  className,
}: StatusIndicatorProps) {
  const { tone, icon: Glyph, label } = STATES[status];

  return (
    <span className={cx(styles.status, styles[tone], className)}>
      <Glyph size={14} className={styles.glyph} />
      {showLabel ? (
        <span className={styles.statusLabel}>{children ?? label}</span>
      ) : (
        <span className={styles.visuallyHidden}>{label}</span>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------------- *
 * Badge
 * ---------------------------------------------------------------------- */

export interface BadgeProps {
  tone?: Tone;
  /**
   * A quieter badge: text and a hairline, no fill.
   *
   * The default in dense lists. A filled badge is a block of colour, and a
   * column of them turns a table into a chart nobody asked for.
   */
  variant?: 'subtle' | 'outline';
  children: ReactNode;
  className?: string;
  title?: string;
}

export function Badge({
  tone = 'neutral',
  variant = 'subtle',
  children,
  className,
  title,
}: BadgeProps) {
  return (
    <span
      className={cx(styles.badge, styles[tone], styles[variant], className)}
      {...(title ? { title } : {})}
    >
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------------- *
 * Count
 * ---------------------------------------------------------------------- */

export interface CountProps {
  value: number;
  tone?: Tone;
  /** What the number counts, for assistive technology: "unread notifications". */
  label: string;
  /**
   * A one- or two-character visible marker before the number.
   *
   * Needed wherever counts sit in a row with no column header — a severity
   * tally of `0 2 7 11` is four numbers nobody can read. The marker is
   * `aria-hidden`, because `label` already says the same thing properly.
   */
  marker?: string;
  className?: string;
}

/**
 * A number with a tone — a severity tally, an unread count.
 *
 * Tabular figures, because these appear in columns and proportional digits make
 * a column of numbers look ragged. Zero renders in the neutral tone whatever
 * tone was asked for: zero criticals is not a critical thing, and a red `0` is
 * the single most common way a security summary lies at a glance.
 */
export function Count({ value, tone = 'neutral', label, marker, className }: CountProps) {
  const effective: Tone = value === 0 ? 'neutral' : tone;
  return (
    <span className={cx(styles.count, styles[effective], className)} title={`${value} ${label}`}>
      {marker ? (
        <span className={styles.countMarker} aria-hidden="true">
          {marker}
        </span>
      ) : null}
      <span aria-hidden="true">{value}</span>
      <span className={styles.visuallyHidden}>
        {value} {label}
      </span>
    </span>
  );
}

/* ---------------------------------------------------------------------- *
 * Domain badges (DL-245)
 *
 * Each is a Badge with the vocabulary decided once: an incident's severity
 * and state, an SLO's evaluation state, a service's lifecycle, and a lineage
 * hop's evidence. The words are the API's enums; the tones are chosen here
 * so that two screens never disagree about whether "mitigated" is amber.
 * ---------------------------------------------------------------------- */

export type IncidentSeverityName = 'critical' | 'high' | 'medium' | 'low';

const SEVERITY: Record<IncidentSeverityName, { tone: Tone; label: string }> = {
  critical: { tone: 'danger', label: 'Critical' },
  high: { tone: 'danger', label: 'High' },
  medium: { tone: 'warning', label: 'Medium' },
  low: { tone: 'neutral', label: 'Low' },
};

export function SeverityBadge({ severity, className }: { severity: string; className?: string }) {
  const known = Object.hasOwn(SEVERITY, severity)
    ? SEVERITY[severity as IncidentSeverityName]
    : { tone: 'neutral' as Tone, label: severity };
  return (
    <Badge tone={known.tone} {...(className ? { className } : {})}>
      {known.label}
    </Badge>
  );
}

export type IncidentStateName = 'open' | 'investigating' | 'mitigated' | 'resolved' | 'closed';

export const INCIDENT_STATE_LABEL: Record<IncidentStateName, string> = {
  open: 'Open',
  investigating: 'Investigating',
  mitigated: 'Mitigated',
  resolved: 'Resolved',
  closed: 'Closed',
};

const INCIDENT_STATE_TONE: Record<IncidentStateName, Tone> = {
  open: 'danger',
  investigating: 'warning',
  mitigated: 'warning',
  resolved: 'success',
  closed: 'done',
};

export function IncidentStateBadge({ state, className }: { state: string; className?: string }) {
  const known = Object.hasOwn(INCIDENT_STATE_LABEL, state);
  const s = state as IncidentStateName;
  return (
    <Badge tone={known ? INCIDENT_STATE_TONE[s] : 'neutral'} {...(className ? { className } : {})}>
      {known ? INCIDENT_STATE_LABEL[s] : state}
    </Badge>
  );
}

export type SloStateName =
  'ok' | 'insufficient_data' | 'stale' | 'service_archived' | 'not_configured';

export const SLO_STATE: Record<SloStateName, { tone: Tone; label: string; detail: string }> = {
  ok: { tone: 'success', label: 'Evaluated', detail: 'Buckets cover the window.' },
  insufficient_data: {
    tone: 'warning',
    label: 'Insufficient data',
    detail: 'Too few buckets in the window to state a level.',
  },
  stale: {
    tone: 'warning',
    label: 'Stale',
    detail: 'The newest bucket is older than it should be.',
  },
  service_archived: {
    tone: 'done',
    label: 'Service archived',
    detail: 'Evaluation stopped when the service was archived.',
  },
  not_configured: {
    tone: 'neutral',
    label: 'Not configured',
    detail: 'No telemetry selector covers this environment.',
  },
};

export function SloStateBadge({ state, className }: { state: SloStateName; className?: string }) {
  const s = SLO_STATE[state];
  return (
    <Badge tone={s.tone} title={s.detail} {...(className ? { className } : {})}>
      {s.label}
    </Badge>
  );
}

export type ServiceLifecycleName = 'active' | 'deprecated' | 'archived';

const LIFECYCLE: Record<ServiceLifecycleName, { tone: Tone; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  deprecated: { tone: 'warning', label: 'Deprecated' },
  archived: { tone: 'done', label: 'Archived' },
};

export function ServiceLifecycleBadge({
  lifecycle,
  className,
}: {
  lifecycle: ServiceLifecycleName;
  className?: string;
}) {
  return (
    <Badge tone={LIFECYCLE[lifecycle].tone} {...(className ? { className } : {})}>
      {LIFECYCLE[lifecycle].label}
    </Badge>
  );
}

/**
 * How a lineage hop is known (DL-244 item 4).
 *
 * `verified` only when provenance was checked; `recorded` for a link the
 * record states; `derived` for a join the server made (a binding); `unknown`
 * when the hop is missing. Each carries a glyph as well as a tone, so the
 * difference survives colour-blindness and a monochrome print (§8).
 */
export type LineageState = 'verified' | 'recorded' | 'derived' | 'unknown';

export const LINEAGE_STATE: Record<
  LineageState,
  { tone: Tone; label: string; icon: (p: IconProps) => ReactNode; detail: string }
> = {
  verified: {
    tone: 'success',
    label: 'Verified',
    icon: CheckCircle,
    detail: 'Provenance was checked against the artifact.',
  },
  recorded: {
    tone: 'info',
    label: 'Recorded',
    icon: Dot,
    detail: 'The record states this link; it was not independently verified.',
  },
  derived: {
    tone: 'accent',
    label: 'Derived',
    icon: Info,
    detail: 'Joined from bindings rather than stated by the record.',
  },
  unknown: {
    tone: 'neutral',
    label: 'Unknown',
    icon: Dash,
    detail: 'The platform does not hold this hop.',
  },
};

export function LineageStateBadge({
  state,
  reason,
  className,
}: {
  state: LineageState;
  /** Why, when the generic sentence is not the whole story. */
  reason?: string | undefined;
  className?: string;
}) {
  const { tone, label, icon: Glyph, detail } = LINEAGE_STATE[state];
  return (
    <Badge tone={tone} title={reason ?? detail} {...(className ? { className } : {})}>
      <Glyph size={12} className={styles.glyph} /> {label}
    </Badge>
  );
}
